import { useEffect, useRef, useState } from "react"

interface Props {
  value: string
  onPlaceSelected: (address: string, lat: number, lng: number) => void
  onClear?: () => void
  placeholder?: string
  wrapperClassName?: string
  inputClassName?: string
}

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""

let scriptState: "idle" | "loading" | "loaded" = "idle"
const pendingCallbacks: (() => void)[] = []

function loadGoogleMapsScript(callback: () => void) {
  if (scriptState === "loaded") {
    callback()
    return
  }
  pendingCallbacks.push(callback)
  if (scriptState === "loading") return
  scriptState = "loading"
  const script = document.createElement("script")
  script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&language=ro`
  script.async = true
  script.onload = () => {
    scriptState = "loaded"
    pendingCallbacks.forEach((cb) => cb())
    pendingCallbacks.length = 0
  }
  document.head.appendChild(script)
}

export const PlacesAutocomplete = ({ value, onPlaceSelected, onClear, placeholder, wrapperClassName, inputClassName }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<any>(null)
  const [inputValue, setInputValue] = useState(value)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  useEffect(() => {
    loadGoogleMapsScript(() => {
      if (!inputRef.current) return
      const g = (window as any).google
      autocompleteRef.current = new g.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: "ro" },
        fields: ["formatted_address", "geometry"],
        types: ["address"],
      })
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace()
        if (!place.geometry?.location) return
        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        setInputValue(place.formatted_address)
        onPlaceSelected(place.formatted_address, lat, lng)
      })
    })
    return () => {
      if (autocompleteRef.current) {
        const g = (window as any).google
        g?.maps?.event?.clearInstanceListeners(autocompleteRef.current)
      }
    }
  }, [])

  const handleClear = () => {
    setInputValue("")
    onClear?.()
  }

  return (
    <div className={wrapperClassName || "relative mt-4"}>
      <input
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={placeholder || "Caută adresa..."}
        className={inputClassName || "w-full rounded-[4px] border border-[#cccccc] px-3 py-[9px] text-[16px] focus:border-[#2684ff] focus:outline-none focus:ring-1 focus:ring-[#2684ff]"}
      />
      {inputValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
        >
          ×
        </button>
      )}
    </div>
  )
}
